// Adjacency indices over a Tree. Built lazily and cached per Tree object, so any code
// can call graphOf(tree) freely; a new Tree object (after an edit) gets a new index.

import type { Group, Id, ParentRel, PartnerRel, SiblingRel, SiblingType, SocialRel, Tree } from './types';

export interface Graph {
  tree: Tree;
  /** Links where id is the child (rel.b === id). */
  parentRels: Map<Id, ParentRel[]>;
  /** Links where id is the parent (rel.a === id). */
  childRels: Map<Id, ParentRel[]>;
  partnerRels: Map<Id, PartnerRel[]>;
  siblingRels: Map<Id, SiblingRel[]>;
  socialRels: Map<Id, SocialRel[]>;
  groupsOf: Map<Id, Group[]>;
}

const cache = new WeakMap<Tree, Graph>();

function push<K, V>(m: Map<K, V[]>, k: K, v: V) {
  const arr = m.get(k);
  if (arr) arr.push(v);
  else m.set(k, [v]);
}

export function graphOf(tree: Tree): Graph {
  const hit = cache.get(tree);
  if (hit) return hit;
  const g: Graph = {
    tree,
    parentRels: new Map(),
    childRels: new Map(),
    partnerRels: new Map(),
    siblingRels: new Map(),
    socialRels: new Map(),
    groupsOf: new Map(),
  };
  for (const r of Object.values(tree.relationships)) {
    // Skip dangling links rather than crash; they can appear after partial imports.
    if (!tree.people[r.a] || !tree.people[r.b]) continue;
    switch (r.kind) {
      case 'parent':
        push(g.parentRels, r.b, r);
        push(g.childRels, r.a, r);
        break;
      case 'partner':
        push(g.partnerRels, r.a, r);
        push(g.partnerRels, r.b, r);
        break;
      case 'sibling':
        push(g.siblingRels, r.a, r);
        push(g.siblingRels, r.b, r);
        break;
      case 'social':
        push(g.socialRels, r.a, r);
        push(g.socialRels, r.b, r);
        break;
    }
  }
  for (const grp of Object.values(tree.groups)) {
    for (const m of grp.members) if (tree.people[m.personId]) push(g.groupsOf, m.personId, grp);
  }
  cache.set(tree, g);
  return g;
}

export const other = (r: { a: Id; b: Id }, id: Id): Id => (r.a === id ? r.b : r.a);

export function parentsOf(g: Graph, id: Id): Id[] {
  return (g.parentRels.get(id) ?? []).map((r) => r.a);
}

export function childrenOf(g: Graph, id: Id): Id[] {
  return (g.childRels.get(id) ?? []).map((r) => r.b);
}

/** Someone's children who have no other parent recorded. */
export function childrenWithNoOtherParent(g: Graph, id: Id): Id[] {
  return childrenOf(g, id).filter((c) => parentsOf(g, c).every((p) => p === id));
}

export function partnersOf(g: Graph, id: Id): Id[] {
  return [...new Set((g.partnerRels.get(id) ?? []).map((r) => other(r, id)))];
}

export interface SiblingInfo {
  id: Id;
  type: SiblingType;
  sharedParents: Id[];
  explicit?: SiblingRel;
}

/** Siblings derived from shared parents, merged with explicit sibling links. */
export function siblingsOf(g: Graph, id: Id): SiblingInfo[] {
  const myParents = parentsOf(g, id);
  const out = new Map<Id, SiblingInfo>();
  for (const p of myParents) {
    for (const c of childrenOf(g, p)) {
      if (c === id) continue;
      const info = out.get(c) ?? { id: c, type: 'unknown' as SiblingType, sharedParents: [] };
      if (!info.sharedParents.includes(p)) info.sharedParents.push(p);
      out.set(c, info);
    }
  }
  const myRels = g.parentRels.get(id) ?? [];
  for (const info of out.values()) {
    const theirRels = g.parentRels.get(info.id) ?? [];
    // If every shared parent is a chosen/step/foster parent (for either sibling), say so.
    const SPECIAL = ['chosen', 'step', 'foster'] as const;
    const perParent = info.sharedParents.map((p) => {
      const types = [...myRels, ...theirRels].filter((r) => r.a === p).map((r) => r.type);
      return SPECIAL.find((t) => types.includes(t));
    });
    const special = perParent.every(Boolean) ? SPECIAL.find((t) => perParent.includes(t)) : undefined;
    if (special) info.type = special;
    else if (info.sharedParents.length >= 2) info.type = 'full';
    else if (myRels.length >= 2 && theirRels.length >= 2) info.type = 'half';
  }
  for (const r of g.siblingRels.get(id) ?? []) {
    const o = other(r, id);
    const info = out.get(o) ?? { id: o, type: r.type, sharedParents: [] };
    info.explicit = r;
    if (r.type !== 'unknown') info.type = r.type;
    out.set(o, info);
  }
  return [...out.values()];
}

export function ancestorsOf(g: Graph, id: Id, maxDepth = Infinity): Map<Id, number> {
  const depth = new Map<Id, number>([[id, 0]]);
  let frontier = [id];
  for (let d = 1; d <= maxDepth && frontier.length; d++) {
    const next: Id[] = [];
    for (const x of frontier)
      for (const p of parentsOf(g, x))
        if (!depth.has(p)) {
          depth.set(p, d);
          next.push(p);
        }
    frontier = next;
  }
  depth.delete(id);
  return depth;
}

export function descendantsOf(g: Graph, id: Id, maxDepth = Infinity): Map<Id, number> {
  const depth = new Map<Id, number>([[id, 0]]);
  let frontier = [id];
  for (let d = 1; d <= maxDepth && frontier.length; d++) {
    const next: Id[] = [];
    for (const x of frontier)
      for (const c of childrenOf(g, x))
        if (!depth.has(c)) {
          depth.set(c, d);
          next.push(c);
        }
    frontier = next;
  }
  depth.delete(id);
  return depth;
}

/** Every person directly linked to id by any relationship or shared group. */
export function neighborsOf(g: Graph, id: Id, opts: { groups?: boolean } = {}): Id[] {
  const s = new Set<Id>();
  for (const r of g.parentRels.get(id) ?? []) s.add(r.a);
  for (const r of g.childRels.get(id) ?? []) s.add(r.b);
  for (const m of [g.partnerRels, g.siblingRels, g.socialRels]) for (const r of m.get(id) ?? []) s.add(other(r, id));
  if (opts.groups) for (const grp of g.groupsOf.get(id) ?? []) for (const m of grp.members) s.add(m.personId);
  s.delete(id);
  return [...s];
}

/** Sizes of the connected components over all relationship kinds (ignoring groups), largest first. */
export function componentSizes(g: Graph): number[] {
  const seen = new Set<Id>();
  const sizes: number[] = [];
  for (const id of Object.keys(g.tree.people)) {
    if (seen.has(id)) continue;
    let size = 0;
    const stack = [id];
    seen.add(id);
    while (stack.length) {
      const x = stack.pop()!;
      size++;
      for (const n of neighborsOf(g, x))
        if (!seen.has(n)) {
          seen.add(n);
          stack.push(n);
        }
    }
    sizes.push(size);
  }
  return sizes.sort((a, b) => b - a);
}

export function componentCount(g: Graph): number {
  return componentSizes(g).length;
}
